import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  User, Pencil, Save, X, Loader2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/apiClient';
import { API_URLS } from '@/constants/urls';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryKeyFactory';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';
import DashboardError from '../error/DashboardError';

const ProfilePage = () => {

  const { profileData, isLoadingProfileData, isErrorProfileData } = useAuth();


  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [profile, setProfile] = useState({
    name: profileData?.name,
    email: profileData?.email,
    address_line_1: profileData?.address_line_1,
    city: profileData?.city,
    state: profileData?.state,
    postal_code: profileData?.postal_code,
    country: profileData?.country,
    gst: profileData?.gst,
  });

  const [editedProfile, setEditedProfile] = useState({});
  const queryClient = useQueryClient();


  const handleEdit = () => {
    setEditedProfile({ ...profile });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile({});
  };

  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  function isValidGST(gst) {
    return gstRegex.test(gst);
  }

  const handleSave = async () => {


    if (editedProfile?.gst?.trim()) {
      if (!isValidGST(editedProfile?.gst)) {
        toast.error("Enter a valid gst number")
        return
      }
    }


    setSaving(true);
    try {

      const res = await apiClient.put(API_URLS.UPDATE_PROFILE, {
        name: editedProfile?.name,
        address_line1: editedProfile?.address_line_1,
        city: editedProfile?.city,
        state: editedProfile?.state,
        country: editedProfile?.country,
        postal_code: editedProfile?.postal_code,
        gst: editedProfile?.gst
      })

      await queryClient.invalidateQueries({
        queryKey: queryKeys.me()
      })
      toast.success('Profile updated successfully');

    } catch (error) {
      apiErrorHandler(error)
    } finally {
      setSaving(false);
      setIsEditing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedProfile(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoadingProfileData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (isErrorProfileData) {
    return <DashboardError
      title='Unable to load Profile page'
      section='profile'
    />
  }

  const displayData = isEditing ? editedProfile : profileData;


  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Profile</h1>
        <p className="text-muted-foreground">Manage your account information and preferences</p>
      </div>

      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/10">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>Your personal and business details</CardDescription>
              </div>
            </div>
            {!isEditing ? (
              <Button variant="outline" onClick={handleEdit}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCancel} disabled={saving}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Name</Label>
              {isEditing ? (
                <Input
                  value={displayData?.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              ) : (
                <p className="font-medium">{displayData?.name}</p>
              )}
            </div>

            {/* Address Line 1 */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Address Line 1</Label>
              {isEditing ? (
                <Input
                  value={displayData?.address_line_1}
                  onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                />
              ) : (
                <p className="font-medium">{displayData?.address_line_1 || '-'}</p>
              )}
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">City</Label>
              {isEditing ? (
                <Input
                  value={displayData?.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              ) : (
                <p className="font-medium">{displayData?.city}</p>
              )}
            </div>

            {/* State */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">State</Label>
              {isEditing ? (
                <Input
                  value={displayData?.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
              ) : (
                <p className="font-medium">{displayData?.state}</p>
              )}
            </div>

            {/* Postal Code */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Postal Code</Label>
              {isEditing ? (
                <Input
                  value={displayData?.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e.target.value)}
                />
              ) : (
                <p className="font-medium">{displayData?.postal_code}</p>
              )}
            </div>

            {/* GST */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">GST</Label>
              {isEditing ? (
                <Input
                  value={displayData?.gst}
                  onChange={(e) => handleInputChange('gst', e.target.value)}
                  placeholder="Enter GST number"
                />
              ) : (
                <p className="font-medium">{displayData?.gst || '-'}</p>
              )}
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Country</Label>
              {isEditing ? (
                <Input
                  value={displayData?.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  placeholder="Enter country "
                />
              ) : (
                <p className="font-medium">{displayData?.country || '-'}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Activity Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-emerald-100">
              <Calendar className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle>Account Activity</CardTitle>
              <CardDescription>Your account status and creation details</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Active Status */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Active</Label>
              <div>
                {profileData?.has_completed_setup ? (
                  <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge>
                ) : (
                  <Badge variant="destructive">No</Badge>
                )}
              </div>
            </div>

            {/* Account Created */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Account Created</Label>
              <p className="font-medium">{formatDate(profileData?.created_at)}</p>
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
